/**
 * TopSpendersReport — the first relational report (MVP).
 *
 * Shows: top N fans by lifetime spend, with creators-paid count, top-creator
 * concentration, and a click-to-drill-in for full per-creator breakdown.
 *
 * Spec: docs/REPORTS.md — Report 1.
 */
import React, { useState } from "react";
import { tokensV2 as T } from "./tokens.v2";
import { useTopSpenders, buildTopSpendersCSV } from "./useTopSpenders.v2";
import { FanDetailModal } from "./FanDetailModal.v2";
import { showProvenance } from "./env.v2";

// ───────────────────────────────────────────────
//  Formatters
// ───────────────────────────────────────────────
function formatINR(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  if (num >= 1e7) return "Rs " + (num / 1e7).toFixed(2) + "Cr";
  if (num >= 1e5) return "Rs " + (num / 1e5).toFixed(2) + "L";
  if (num >= 1e3) return "Rs " + (num / 1e3).toFixed(1) + "K";
  return "Rs " + Math.round(num);
}
function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ───────────────────────────────────────────────
//  Filter bar
// ───────────────────────────────────────────────
function FilterBar({ filters, onChange, onRefresh, loading, onExport }) {
  const [draft, setDraft] = useState(filters);
  React.useEffect(() => setDraft(filters), [filters]);

  const apply = () => {
    onChange({
      from: draft.from,
      to: draft.to,
      limit: parseInt(draft.limit, 10) || 50,
      minSpend: parseFloat(draft.minSpend) || 0,
    });
  };

  const inputStyle = {
    background: T.surface.card,
    border: `1px solid ${T.border.subtle}`,
    borderRadius: 8, padding: "6px 10px",
    fontSize: 12, color: T.text.primary,
    fontFamily: T.font.sans, outline: "none",
    minWidth: 0,
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
    textTransform: "uppercase", color: T.text.muted,
  };

  return (
    <div style={{
      background: T.surface.card,
      border: `1px solid ${T.border.subtle}`,
      borderRadius: 12,
      padding: 14, marginBottom: 14,
      display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end",
    }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={labelStyle}>From</span>
        <input type="date" value={draft.from || ""} onChange={(e) => setDraft({ ...draft, from: e.target.value })} style={inputStyle} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={labelStyle}>To</span>
        <input type="date" value={draft.to || ""} onChange={(e) => setDraft({ ...draft, to: e.target.value })} style={inputStyle} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={labelStyle}>Top N</span>
        <input type="number" min={1} max={500} value={draft.limit} onChange={(e) => setDraft({ ...draft, limit: e.target.value })} style={{ ...inputStyle, width: 80 }} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={labelStyle}>Min spend (Rs)</span>
        <input type="number" min={0} step={1000} value={draft.minSpend} onChange={(e) => setDraft({ ...draft, minSpend: e.target.value })} style={{ ...inputStyle, width: 110 }} />
      </div>
      <button type="button" onClick={apply} style={{
        background: T.brand.base, color: "#FFFFFF", border: "none",
        borderRadius: 8, padding: "8px 14px",
        fontSize: 12, fontWeight: 700, fontFamily: T.font.sans, cursor: "pointer",
      }}>Apply filters</button>
      <button type="button" onClick={onRefresh} disabled={loading} style={{
        background: T.surface.card, border: `1px solid ${T.border.subtle}`,
        borderRadius: 8, padding: "8px 14px",
        fontSize: 12, fontWeight: 700, color: T.text.secondary,
        cursor: loading ? "not-allowed" : "pointer", fontFamily: T.font.sans,
        opacity: loading ? 0.6 : 1,
      }}>Refresh</button>
      <button type="button" onClick={onExport} style={{
        background: T.surface.card, border: `1px solid ${T.border.subtle}`,
        borderRadius: 8, padding: "8px 14px",
        fontSize: 12, fontWeight: 700, color: T.text.primary,
        cursor: "pointer", fontFamily: T.font.sans,
      }}>↓ Download CSV</button>
    </div>
  );
}

// ───────────────────────────────────────────────
//  Table
// ───────────────────────────────────────────────
function ConcentrationBar({ pct }) {
  const fg = pct > 60 ? T.status.bad : pct > 40 ? T.status.warn : T.status.good;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div style={{
        flex: 1, height: 6, background: T.surface.canvas, borderRadius: 999, overflow: "hidden",
        minWidth: 60,
      }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", background: fg, borderRadius: 999 }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color: fg, fontVariantNumeric: "tabular-nums", minWidth: 40, textAlign: "right" }}>
        {pct?.toFixed(1)}%
      </span>
    </div>
  );
}

function TableRow({ r, rank, onOpen }) {
  return (
    <tr onClick={() => onOpen(r.fanHandle)} style={{
      cursor: "pointer", borderBottom: `1px solid ${T.border.subtle}`,
      transition: "background-color 120ms ease",
    }}
    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = T.surface.cardHover}
    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = "transparent"}
    >
      <td style={{ padding: "10px 12px", fontSize: 12, fontWeight: 600, color: T.text.muted, fontVariantNumeric: "tabular-nums" }}>{rank}</td>
      <td style={{ padding: "10px 12px", fontSize: 13 }}>
        <div style={{ fontWeight: 700, color: T.text.primary }}>{r.name || r.fanHandle}</div>
        <div style={{ fontFamily: T.font.mono, fontSize: 10, color: T.text.muted }}>{r.fanHandle}</div>
      </td>
      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: T.text.primary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatINR(r.totalSpent)}</td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: T.text.secondary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.creatorsPaid}</td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: T.text.secondary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.callCount}</td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: T.text.secondary, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{formatINR(r.avgPerCall)}</td>
      <td style={{ padding: "10px 12px", fontSize: 12, color: T.text.primary, fontWeight: 600 }}>{r.topCreatorName}</td>
      <td style={{ padding: "10px 12px", minWidth: 130 }}><ConcentrationBar pct={r.topCreatorPct || 0} /></td>
      <td style={{ padding: "10px 12px", fontSize: 11, color: T.text.muted }}>{formatDate(r.lastCallAt)}</td>
      <td style={{ padding: "10px 12px", fontSize: 11, color: r.lifetimeRefunds > 0 ? T.status.warn : T.text.muted, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.lifetimeRefunds}</td>
    </tr>
  );
}

// ───────────────────────────────────────────────
//  Page
// ───────────────────────────────────────────────
export function TopSpendersReport() {
  const today = new Date().toISOString().slice(0, 10);
  const yearAgo = new Date(); yearAgo.setFullYear(yearAgo.getFullYear() - 1);
  const yearAgoStr = yearAgo.toISOString().slice(0, 10);

  const [filters, setFilters] = useState({ from: yearAgoStr, to: today, limit: 50, minSpend: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const [openFanHandle, setOpenFanHandle] = useState(null);

  const { data, loading, error } = useTopSpenders(filters, refreshKey);

  const onRefresh = () => setRefreshKey((k) => k + 1);
  const onExport = () => {
    if (!data) return;
    const csv = buildTopSpendersCSV(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `top-spending-fans-${filters.from}-to-${filters.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <div style={{
        background: T.surface.card,
        border: `1px solid ${T.border.subtle}`,
        borderLeft: `4px solid #FC8019`,
        borderRadius: 12,
        padding: 16, marginBottom: 14,
        display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.text.primary, letterSpacing: "-0.01em" }}>Top Spending Fans</div>
          <div style={{ fontSize: 12, color: T.text.muted, marginTop: 2 }}>
            Whales by spend in the selected window. Click any row to see their full per-creator breakdown.
          </div>
        </div>
      </div>

      <FilterBar filters={filters} onChange={setFilters} onRefresh={onRefresh} loading={loading} onExport={onExport} />

      {error && (
        <div style={{
          fontSize: 12, color: T.text.secondary,
          background: T.status.warnBg, border: `1px solid ${T.status.warn}`,
          padding: "8px 12px", borderRadius: 8, marginBottom: 12,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        }}>
          <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.status.warn }}>● Backend error</span>
          <span style={{ flex: 1 }}><strong>Could not fetch live data:</strong> {error}.</span>
          <button type="button" onClick={onRefresh} style={{
            background: T.surface.card, border: `1px solid ${T.border.subtle}`,
            borderRadius: 8, padding: "5px 12px",
            fontSize: 12, fontWeight: 700, color: T.text.primary, cursor: "pointer",
          }}>Retry</button>
        </div>
      )}

      <div style={{
        background: T.surface.card,
        border: `1px solid ${T.border.subtle}`,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 14,
      }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: T.font.sans }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${T.border.subtle}`, background: T.surface.canvas }}>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "left" }}>#</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "left" }}>Fan</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "right" }}>Total spent</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "right" }}>Creators</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "right" }}>Calls</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "right" }}>Avg/call</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "left" }}>Top creator</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "left" }}>% to top</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "left" }}>Last call</th>
                <th style={{ padding: "10px 12px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.text.muted, textAlign: "right" }}>Refunds</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((r, i) => (
                <TableRow key={r.fanHandle} r={r} rank={i + 1} onOpen={setOpenFanHandle} />
              ))}
              {(!data || data.rows.length === 0) && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: T.text.muted, fontSize: 13 }}>{loading ? "Loading…" : "No fans matched the filter."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {data && data.totals && (
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          fontSize: 12, color: T.text.muted, padding: "0 4px 12px",
        }}>
          <span>{data.totals.rowCount} fans · combined lifetime spend {formatINR(data.totals.sumSpent)}</span>
          {data.refreshedAt && <span>Refreshed {new Date(data.refreshedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })} IST</span>}
        </div>
      )}

      {showProvenance() && data?.source && data.source.tables && (
        <div style={{
          padding: 12,
          background: T.surface.canvas,
          border: `1px dashed ${T.border.default}`,
          borderRadius: 10,
          fontSize: 11, color: T.text.muted, fontFamily: T.font.mono,
          marginBottom: 14,
        }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: T.text.secondary }}>Where this data comes from (dev only)</div>
          <div>Tables: {data.source.tables.join(" · ")}</div>
          {data.source.query && <div>Query: {data.source.query}</div>}
        </div>
      )}

      {openFanHandle && (
        <FanDetailModal
          fanHandle={openFanHandle}
          onClose={() => setOpenFanHandle(null)}
          fromDate={filters.from}
          toDate={filters.to}
        />
      )}
    </>
  );
}
